import { FC, useLayoutEffect } from 'react';
import { useHistory } from 'react-router';

import { FeatureName } from '__generated__/GQL';
import useCanCompanyUserAccessFeature from 'common/hooks/useCanCompanyUserAccessFeature';
import { useCompanyBankAccounts } from 'common/hooks/useCompanyBankAccounts';
import useFeatureFlagsSwitcher from 'common/hooks/useFeatureFlagsSwitcher';
import TrackView from 'features/Analytics/TrackView';
import * as Layout from 'layouts/ContentLayout';

import AccountHeader from './AccountHeader';
import LegacyReceiptPreview from './components/LegacyReceiptPreview';
import ReceiptPreviewModal from './components/ReceiptPreviewModal';
import { useBankOverviewSearchParams } from './hooks/useBankOverviewSearchParams';
import { TransactionsFiltersProvider } from './hooks/useTransactionsFilters';
import Sidebar from './Sidebar';
import Transactions from './Transactions';

const Overview: FC = () => {
  const { featureFlags } = useFeatureFlagsSwitcher();
  const { canAccessFeature } = useCanCompanyUserAccessFeature();
  const {
    bankAccountId,
    getTransactionPath,
    receiptId,
    setBankAccountId,
    showReceipt,
    transactionId,
  } = useBankOverviewSearchParams();
  const { bankAccounts } = useCompanyBankAccounts();
  const history = useHistory();

  useLayoutEffect(() => {
    if (bankAccounts.length === 1 && bankAccounts[0] && !bankAccountId) {
      setBankAccountId(bankAccounts[0].bankAccountId);
    }
  }, [setBankAccountId, bankAccountId, bankAccounts]);

  const onCloseReceiptPreviewModal = () => {
    if (transactionId) {
      history.push(getTransactionPath(transactionId));
    }
  };

  /**
   * @todo Remove showReceipt condition after multi-doc feature release
   * @link https://linear.app/shine/issue/CAC-58
   */
  const shouldShowReceiptPreview = transactionId && (showReceipt || receiptId);

  return (
    <TransactionsFiltersProvider>
      <TrackView pageName="Bank" />
      <Layout.Root>
        <Layout.Body size="md">
          {canAccessFeature({ name: FeatureName.BankInfo }) ? (
            <AccountHeader />
          ) : null}
          <Transactions />
        </Layout.Body>
        {transactionId ? (
          <Layout.Sidebar
            borderLeft="1px solid"
            borderLeftColor="border"
            isScrollable
          >
            <TrackView pageName="BankTransactionDetail" />
            <Sidebar transactionId={transactionId} />
          </Layout.Sidebar>
        ) : null}
        {shouldShowReceiptPreview ? (
          <>
            <TrackView pageName="BankReceiptPreview" />
            {featureFlags.enableMultipleTransactionDocs && receiptId ? (
              <ReceiptPreviewModal
                onClose={onCloseReceiptPreviewModal}
                receiptId={receiptId}
              />
            ) : (
              <LegacyReceiptPreview
                closeUrl={getTransactionPath(transactionId)}
                transactionId={transactionId}
              />
            )}
          </>
        ) : null}
      </Layout.Root>
    </TransactionsFiltersProvider>
  );
};

export default Overview;
